import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useDocument, useUpdateDocument, useDeleteDocument } from '@/hooks/useDocuments'
import { getPresignedUrl, approveDocument } from '@/services/documentsApi'
import { MetadataForm } from './MetadataForm'
import { RelatedDocuments } from './RelatedDocuments'
import { SumitMetadata } from './SumitMetadata'
import type { Document } from '@/types'

interface DocumentDetailProps {
  documentId: string
  onBack: () => void
  onDocumentSelect: (id: string) => void
}

export function DocumentDetail({
  documentId,
  onBack,
  onDocumentSelect,
}: DocumentDetailProps) {
  const queryClient = useQueryClient()
  const { data: document, isLoading, error } = useDocument(documentId)
  const updateMutation = useUpdateDocument()
  const deleteMutation = useDeleteDocument()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [approveError, setApproveError] = useState<string | null>(null)

  const handleApprove = async () => {
    setIsApproving(true)
    setApproveError(null)
    try {
      await approveDocument(documentId)
      queryClient.invalidateQueries({ queryKey: ['document', documentId] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to approve document'
      setApproveError(message)
    } finally {
      setIsApproving(false)
    }
  }

  const handleSave = (data: Partial<Document>) => {
    updateMutation.mutate({ id: documentId, data })
  }

  const handleDownload = async () => {
    if (!document) return
    try {
      const url = await getPresignedUrl(document.s3_key)
      window.open(url, '_blank')
    } catch (err) {
      console.error('Failed to get download URL:', err)
    }
  }

  const handleDelete = () => {
    deleteMutation.mutate(documentId, {
      onSuccess: () => {
        onBack()
      },
    })
  }

  if (isLoading) {
    return (
      <div className="p-6" data-testid="detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-3/4" />
        </div>
      </div>
    )
  }

  if (error || !document) {
    return (
      <div className="p-6" data-testid="detail-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load document</p>
          <button
            onClick={onBack}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6" data-testid="document-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-500 hover:text-gray-700"
            data-testid="back-btn"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h2 className="text-xl font-semibold text-gray-900">
            {document.document_number || 'Document Details'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {document.document_status === 'manual_review' && (
            <button
              onClick={handleApprove}
              disabled={isApproving}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="approve-btn"
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </button>
          )}
          <button
            onClick={handleDownload}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            data-testid="download-btn"
          >
            Download
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            data-testid="delete-btn"
          >
            Delete
          </button>
        </div>
      </div>

      {/* Approve Error Message */}
      {approveError && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between"
          data-testid="approve-error"
        >
          <p className="text-red-800 text-sm">{approveError}</p>
          <button
            onClick={() => setApproveError(null)}
            className="text-red-600 hover:text-red-800 text-sm underline ml-4"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Document
            </h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete this document? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                data-testid="cancel-delete-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-delete-btn"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <MetadataForm
              document={document}
              onSave={handleSave}
              isSaving={updateMutation.isPending}
            />
          </div>
        </div>

        <div className="space-y-6">
          {/* Confidence Scores */}
          {(document.extracted_metadata?.business_confidence != null ||
            document.extracted_metadata?.supplier_confidence != null) && (
            <div
              className="bg-white border border-gray-200 rounded-lg p-6"
              data-testid="confidence-scores"
            >
              <h3 className="text-sm font-medium text-gray-500 mb-3">
                Confidence Scores
              </h3>
              <div className="space-y-2">
                {document.extracted_metadata?.business_confidence != null && (
                  <div className="flex items-center justify-between" data-testid="business-confidence">
                    <span className="text-sm text-gray-700">Business</span>
                    <span
                      className={`text-sm font-medium ${
                        (document.extracted_metadata.business_confidence as number) < 0.95
                          ? 'text-orange-600'
                          : 'text-green-600'
                      }`}
                    >
                      {Math.round((document.extracted_metadata.business_confidence as number) * 100)}%
                    </span>
                  </div>
                )}
                {document.extracted_metadata?.supplier_confidence != null && (
                  <div className="flex items-center justify-between" data-testid="supplier-confidence">
                    <span className="text-sm text-gray-700">Supplier</span>
                    <span
                      className={`text-sm font-medium ${
                        (document.extracted_metadata.supplier_confidence as number) < 0.95
                          ? 'text-orange-600'
                          : 'text-green-600'
                      }`}
                    >
                      {Math.round((document.extracted_metadata.supplier_confidence as number) * 100)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {document.extracted_metadata?.sumit_status != null && (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <SumitMetadata document={document} />
            </div>
          )}

          {/* Related documents widget sits below the Sumit status widget */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <RelatedDocuments
              documentId={documentId}
              onDocumentSelect={onDocumentSelect}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
